import {
  Button,
  Fade,
  IconButton,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core'
import { Close } from '@material-ui/icons'
import axios from 'axios'
import { useEffect, useState } from 'react'
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { updateProject } from '../../../actions/projectActions'
import ToastMessage from '../../ToastMessage/ToastMessage'

interface PartRequestTableProps {
  requests: IRequest[]
}

interface RowProps {
  request: IRequest
  index: string
}

const PartRequestTable = ({ requests }: PartRequestTableProps) => {
  const dispatch = useDispatch()
  const history = useHistory()

  const [requestToDeleteIndex, setRequestToDeleteIndex] = useState('')
  const [requestToDeleteName, setRequestToDeleteName] = useState('')
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteToast, setDeleteToast] = useState(false)

  const { userInfo } = useSelector(
    (state: { userLogin: UserLogin }) => state.userLogin
  )
  const { project } = useSelector(
    (state: RootStateOrAny) => state.projectDetails
  )
  const { success: successProjectUpdate } = useSelector(
    (state: RootStateOrAny) => state.projectUpdate
  )

  const config = {
    headers: {
      Authorization: `Bearer ${userInfo.token}`,
    },
  }

  useEffect(() => {
    if (!userInfo) {
      history.push(`/login?redirect=${window.location.pathname}`)
    }

    if (successProjectUpdate) {
      setDeleteToast(true)
    }
  }, [successProjectUpdate, userInfo, history])

  const deleteHandler = () => {
    let projectClone = project

    projectClone.requests.splice(requestToDeleteIndex, 1)

    setShowDeleteModal(false)
    dispatch(updateProject(projectClone))
  }

  const Row = ({ request, index }: RowProps) => {
    const [partName, setPartName] = useState('')
    const [machineName, setMachineName] = useState('')

    useEffect(() => {
      let isCancelled = false

      const getPartDetails = async () => {
        try {
          const { data } = await axios.get(
            `/api/parts/details/${request.part}`,
            config
          )

          if (!isCancelled) {
            setPartName(data.name)
          }
        } catch (error) {
          if (!isCancelled) {
            console.error(error)
          }
        }
      }

      const getMachineDetails = async () => {
        try {
          const { data } = await axios.get(
            `/api/machines/details/${request.machine}`,
            config
          )

          if (!isCancelled) {
            setMachineName(data.name)
          }
        } catch (error) {
          if (!isCancelled) {
            console.error(error)
          }
        }
      }

      if (request.part !== '') {
        getPartDetails()
      }

      if (request.machine !== '') {
        getMachineDetails()
      }

      return () => {
        isCancelled = true
      }
    }, [request])

    const handleClick = () => {
      setRequestToDeleteIndex(index)
      setRequestToDeleteName(partName)
      setShowDeleteModal(true)
    }

    return (
      <TableRow>
        <TableCell>{partName}</TableCell>
        {project.type === 'programming' ? (
          <TableCell>{machineName}</TableCell>
        ) : (
          <TableCell>{request.qty}</TableCell>
        )}

        <TableCell>{request.report.reportType}</TableCell>
        <TableCell className="actions" align="right">
          <IconButton onClick={handleClick}>
            <Close />
          </IconButton>
        </TableCell>
      </TableRow>
    )
  }

  return (
    <>
      {requests.length > 0 && (
        <>
          <div className="m-b-5 m-t-32">
            Selected{' '}
            <span className="gray-text">
              ({requests.length} contract {project.type} request
              {requests.length > 1 && 's'})
            </span>
          </div>
          <TableContainer className="scrollable">
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  <TableCell width="32%">Part name</TableCell>
                  {project.type === 'programming' ? (
                    <TableCell width="32%">Machine name</TableCell>
                  ) : (
                    <TableCell width="32%">Quantity</TableCell>
                  )}
                  <TableCell width="32%">Report requirements</TableCell>
                  <TableCell width="4%"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map((request, index) => (
                  <Row key={index} request={request} index={index.toString()} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      <Modal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        aria-labelledby="Finish adding parts modal"
        aria-describedby="Finish adding parts modal"
      >
        <Fade in={showDeleteModal}>
          <div className="modal-window">
            <div className="modal-header">
              <div>Delete part request?</div>
              <IconButton onClick={() => setShowDeleteModal(false)}>
                <Close />
              </IconButton>
            </div>
            <div className="modal-body">
              <h4 className="m-b-32">Delete part request?</h4>
              <p>
                Are you sure you want to remove {requestToDeleteName} from your
                quote request?
              </p>
              <div className="actions m-t-40 flex-end flex">
                <Button
                  color="primary"
                  variant="outlined"
                  className="m-r-24"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={deleteHandler}
                >
                  Confirm
                </Button>
              </div>
            </div>
          </div>
        </Fade>
      </Modal>

      <ToastMessage
        message={
          <>
            <p className="fs16 fw600">
              <span className="capitalize">{project.type} </span>request removed
            </p>
            <p className="fs14 m-b-0">
              <span className="capitalize">{project.type} </span> requst for{' '}
              {requestToDeleteName} has been removed.
            </p>
          </>
        }
        open={deleteToast}
        setOpen={setDeleteToast}
      />
    </>
  )
}

export default PartRequestTable
